import React, { useEffect, useRef, useState, useMemo } from 'react';
import './Shop.css';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { usePost } from '../../../hooks/usePost';
import { usePaginatedPost } from '../../../hooks/usePaginatedPost';
import { useGet } from '../../../hooks/useGet';
import { useBasketAdd } from '../../../hooks/useBasketAdd';
import { toast } from "react-toastify";
import TelegramSafeArea from '../../../Components/TelegramSafeArea';
import StoreBanner from './StoreBanner';

import ShopHeader from './ShopHeader';
import ShopGroups from './ShopGroups';
import ShopEvents from './ShopEvents';
import ShopProductList from './ShopProductList';
import ScrollToTopButton from './ScrollToTopButton';
import ShopBasketAdd from '../ShopBasketAdd/ShopBasketAdd';

const themeColors = [
    { id: 1, title: "قرمز", code: "#D32F2F" },
    { id: 2, title: "سبز", code: "#388E3C" },
    { id: 3, title: "زرد", code: "#FBC02D" },
    { id: 4, title: "صورتی", code: "#C2185B" },
    { id: 5, title: "آبی", code: "#1976D2" },
    { id: 6, title: "بنفش", code: "#7B1FA2" },
    { id: 7, title: "نارنجی", code: "#E64A19" },
    { id: 8, title: "طوسی", code: "#212121" },
];

const Shop = ({ params, onExitCallback }) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // 🔹 استخراج آیدی فروشگاه و کالا
    const paramShopId = params?.shop || searchParams.get('sId');
    const paramProductId = params?.product || searchParams.get('pId') || searchParams.get('commodityId');

    const [commodityId, setCommodityId] = useState(paramProductId || null);
    const [isOpen, setIsOpen] = useState(Boolean(paramProductId));
    const [isOpenSheet, setIsOpenSheet] = useState(false);

    const [isScrolled, setIsScrolled] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const scrollContainerRef = useRef(null);
    const lastScrollTopRef = useRef(0);

    const [store, setStore] = useState(() => {
        const sessionStore = JSON.parse(sessionStorage.getItem("store"));
        if (paramShopId && sessionStore && String(sessionStore.id) !== String(paramShopId)) {
            return null;
        }
        return sessionStore ?? null;
    });

    const activeStoreId = paramShopId || store?.id;
    const [cartItems, setCartItems] = useState((JSON.parse(localStorage.getItem("baskets")) ?? []).find((x) => String(x.id) === String(store?.id))?.cartItems ?? []);

    const [cursor, setCursor] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState(null);

    const [backgroundcolor, setBackgroundcolor] = useState(null);
    const [color, setColor] = useState(null);
    const sentinelRef = useRef();

    // اسکرول افقی ماوس
    const isDownRef = useRef(false);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);

    const handleMouseDown = (e) => {
        isDownRef.current = true;
        e.currentTarget.classList.add('active');
        startXRef.current = e.pageX - e.currentTarget.offsetLeft;
        scrollLeftRef.current = e.currentTarget.scrollLeft;
    };
    const handleMouseLeave = (e) => {
        isDownRef.current = false;
        e.currentTarget.classList.remove('active');
    };
    const handleMouseUp = (e) => {
        isDownRef.current = false;
        e.currentTarget.classList.remove('active');
    };
    const handleMouseMove = (e) => {
        if (!isDownRef.current) return;
        e.preventDefault();
        const x = e.pageX - e.currentTarget.offsetLeft;
        const walk = (x - startXRef.current) * 1.5;
        e.currentTarget.scrollLeft = scrollLeftRef.current - walk;
    };

    const [urlShop, setUrlShop] = useState(activeStoreId ? `business/show/${activeStoreId}` : null);

    // خروج از فروشگاه و پاک کردن کامل استیت‌ها
    const fn_Exit = () => {
        sessionStorage.removeItem("store");
        setStore(null);
        setCartItems([]);
        setCommodityId(null);
        setIsOpen(false);

        if (typeof onExitCallback === 'function') {
            onExitCallback();
        }

        // تمیز کردن کوئری استرینگ‌ها
        navigate('/HomePage', { replace: true });
    };

    // دریافت داده‌های فروشگاه
    const { data: shop_data, isResult } = useGet(urlShop);

    // دریافت گروه‌ها
    const { data: groups_data } = usePost(
        store ? `commodity/group/show/all` : null,
        { id_business: store ? store.id : -1 }
    );

    const groupsList = useMemo(() => {
        if (!groups_data) return [];
        if (Array.isArray(groups_data?.data)) return groups_data.data;
        if (Array.isArray(groups_data?.data?.data)) return groups_data.data.data;
        if (Array.isArray(groups_data)) return groups_data;
        if (Array.isArray(groups_data?.result)) return groups_data.result;
        return [];
    }, [groups_data]);

    const isGroupsFinished = Boolean(groups_data !== undefined && groups_data !== null);
    const shouldShowGroups = !isGroupsFinished ? true : (groupsList.length > 0);

    // دریافت جشنواره‌ها
    const { data: events_data, loaded: isloaded_events, loading: isloading_events_state } = usePost(
        store ? `level/event/all/commodities` : null,
        { business_id: store ? store.id : -1 }
    );
    const isEventsLoaded = isloaded_events === true || events_data !== undefined || isloading_events_state === false;

    const validEvents = useMemo(() => {
        if (!events_data?.data || !Array.isArray(events_data.data)) return [];
        return events_data.data.filter(event => event.commodities && event.commodities.length > 0);
    }, [events_data]);

    // دریافت کالاهای صفحه با صفحه‌بندی
    const { data: Dataset, loading: isloading_search, isLastPage, nextCursor } = usePaginatedPost(
        store ? `commodity/search/bybusiness` : null,
        {
            id_business: store ? store.id : -1,
            search_text: searchText,
            id_commodity_group: selectedGroupId,
            rowCount: 5
        },
        cursor
    );

    // تم
    const themeColor = useMemo(() => {
        if (!store?.theme_json) return "#1976D2";
        try {
            const themeObj = typeof store.theme_json === 'string' ? JSON.parse(store.theme_json) : store.theme_json;
            const active = themeColors.find(c => c.id === parseInt(themeObj.color_id || 1));
            return active ? active.code : "#1976D2";
        } catch {
            return "#1976D2";
        }
    }, [store]);

    useEffect(() => {
        setCursor(null);
    }, [store?.id]);

    // 🔹 باز کردن خودکار کالا به محض دریافت پارامتر و سپس خنثی کردن آن در URL
    useEffect(() => {
        if (paramProductId) {
            setCommodityId(paramProductId);
            setIsOpen(true);

            // پاک کردن pId از query params بدون لود مجدد صفحه تا در خروج دوباره باز نشود
            if (searchParams.has('pId') || searchParams.has('commodityId')) {
                const nextParams = new URLSearchParams(searchParams);
                nextParams.delete('pId');
                nextParams.delete('commodityId');
                setSearchParams(nextParams, { replace: true });
            }
        }
    }, [paramProductId, searchParams, setSearchParams]);

    // بروزرسانی آدرس دریافت اطلاعات فروشگاه
    useEffect(() => {
        if (paramShopId) {
            setUrlShop(`business/show/${paramShopId}`);
        } else if (store?.id && !urlShop) {
            setUrlShop(`business/show/${store.id}`);
        }

        if (!paramShopId && !store) {
            fn_Exit();
        }
    }, [paramShopId, store?.id]);

    useEffect(() => {
        if (!shop_data) return;
        setStore(prevStore => prevStore && String(prevStore.id) === String(shop_data.id) ? { ...prevStore, ...shop_data } : shop_data);
        sessionStorage.setItem("store", JSON.stringify({
            id: shop_data.id,
            title: shop_data.title,
            message: shop_data.message,
            image_banner: shop_data.image_banner,
            image_logo: shop_data.image_logo,
            send_cost_price: shop_data.send_cost_price,
            card_number: shop_data.card_number,
            theme_json: shop_data.theme_json
        }));
    }, [shop_data]);

    useEffect(() => {
        if (isResult && !shop_data) fn_Exit();
    }, [isResult, shop_data]);

    useEffect(() => {
        if (store) {
            try {
                setCartItems((JSON.parse(localStorage.getItem("baskets")) ?? []).find((x) => String(x.id) === String(store.id))?.cartItems ?? []);
            } catch {
                setCartItems([]);
            }
        }
    }, [store]);

    // IntersectionObserver جهت بارگذاری صفحه بعد
    useEffect(() => {
        if (isloading_search || isLastPage || !nextCursor) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !isloading_search && !isLastPage && nextCursor) {
                setCursor(nextCursor);
            }
        }, { root: null, rootMargin: "200px", threshold: 0 });

        const sentinelNode = sentinelRef.current;
        if (sentinelNode) observer.observe(sentinelNode);
        return () => {
            if (sentinelNode) observer.unobserve(sentinelNode);
        };
    }, [isloading_search, isLastPage, nextCursor]);

    useBasketAdd(store, cartItems);

    const onAdd = (data) => {
        let baskets = localStorage.getItem("baskets") ? JSON.parse(localStorage.getItem("baskets")) : [];
        if (baskets) {
            const basket = baskets.find((x) => String(x.id) === String(store.id));
            if (baskets.length >= 3 && !basket) {
                toast.error('3 فروشگاه سبد باز دارید!!!', { position: "top-left", autoClose: 2000, theme: "light", rtl: "true" });
                setIsOpenSheet(true);
                return;
            }
        }

        const product = data.product;
        const productProperties = data.current;
        let rawPriceVal = productProperties.rawPrice
            ? parseInt(productProperties.rawPrice)
            : (typeof productProperties.price === 'string' ? parseInt(productProperties.price.replace(/,/g, '')) : productProperties.price);

        if (isNaN(rawPriceVal)) rawPriceVal = 0;

        const price = Math.round(rawPriceVal, 0);
        const discount_price = Math.round((price * product.discount_percent) / 100, 0);
        const vat_price = Math.round(((price - discount_price) * product.VAT) / 100, 0);
        const pack_cost = Math.round(parseInt(product.pack_cost || 0), 0);

        const variantId = productProperties.id;
        const exist = cartItems.find((x) => String(x.id) === String(product.id) && String(x.variant_id) === String(variantId));

        if (exist) {
            setCartItems(cartItems.map((x) =>
                String(x.id) === String(product.id) && String(x.variant_id) === String(variantId) ? {
                    ...exist,
                    quantity: exist.quantity + 1,
                    totalPrice: price * (exist.quantity + 1),
                    totalPrice2: (price - discount_price) * (exist.quantity + 1),
                    invoicePrice: ((price - discount_price) + vat_price + pack_cost) * (exist.quantity + 1),
                } : x
            ));
        } else {
            setCartItems([...cartItems, {
                ...product, properies_Id: variantId, variant_id: variantId, p1Name: productProperties.p1Name,
                name: productProperties.name, quantity: 1, price, totalPrice: price,
                discount_price, vat_price, totalPrice2: (price - discount_price) + vat_price,
                pack_cost, invoicePrice: (price - discount_price) + vat_price + pack_cost,
            }]);
        }
        toast.success('به سبد اضافه گردید.', { position: "top-left", autoClose: 500, theme: "light", rtl: "true" });
    };

    const fn_close = () => {
        setCommodityId(null);
        setIsOpen(false);
    };

    const fn_search = () => {
        setCursor(null);
        const trimmed = searchInput.trim();
        setSearchText(trimmed.length > 0 ? trimmed : null);
    };

    const fn_search_close = () => {
        setCursor(null);
        setSearchInput('');
        setSearchText(null);
        const searchEl = document.getElementById('search-text');
        if (searchEl) searchEl.focus();
    };

    const handleGroupClick = (groupId) => {
        setSelectedGroupId((prevId) => (prevId === groupId ? null : groupId));
        setCursor(null);
    };

    const handleBodyScroll = (e) => {
        const scrollTop = e.target.scrollTop;
        if (scrollTop > 150 && !isScrolled) setIsScrolled(true);
        else if (scrollTop <= 20 && isScrolled) setIsScrolled(false);

        const prevY = lastScrollTopRef.current;
        if (scrollTop > 250 && prevY - scrollTop > 6) setShowScrollTop(true);
        else if (scrollTop <= 200 || scrollTop - prevY > 6) setShowScrollTop(false);
        lastScrollTopRef.current = scrollTop;
    };

    const scrollToTop = () => {
        if (window?.Telegram?.WebApp?.HapticFeedback) {
            try { window.Telegram.WebApp.HapticFeedback.impactOccurred('medium'); } catch (_) { }
        }
        if (scrollContainerRef.current) scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
        document.body.scrollTo({ top: 0, behavior: 'smooth' });
        setShowScrollTop(false);
    };

    useEffect(() => {
        if (store?.theme_json) {
            try {
                const themeObj = typeof store.theme_json === 'string' ? JSON.parse(store.theme_json) : store.theme_json;
                const activeColor = themeColors.find(c => c.id === parseInt(themeObj.color_id || 1)) || themeColors[0];
                setBackgroundcolor(`${activeColor.code}1A`);
                setColor('#000');

                if (window?.Telegram?.WebApp) {
                    const tg = window.Telegram.WebApp;
                    tg.setHeaderColor(activeColor.code);
                    if (tg.requestFullscreen) tg.requestFullscreen();
                    else tg.expand();
                    if (tg.isVersionAtLeast('7.10')) tg.setBottomBarColor(activeColor.code);
                }

                let metaThemeColor = document.querySelector("meta[name=theme-color]");
                if (!metaThemeColor) {
                    metaThemeColor = document.createElement("meta");
                    metaThemeColor.name = "theme-color";
                    document.head.appendChild(metaThemeColor);
                }
                metaThemeColor.content = activeColor.code;
            } catch (error) {
                console.error("Error parsing theme JSON:", error);
            }
        }
    }, [store]);

    if (!store && !paramShopId) {
        return <Navigate to="/HomePage" replace={true} />;
    }

    return (
        <>
            <style>
                {`
                    @keyframes spinCustom {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>

            <div className="market" style={{ color, background: backgroundcolor, '--theme-bg': backgroundcolor || '#F9F9F9' }}>
                <TelegramSafeArea className="telegram-safe-area" style={{ background: backgroundcolor, color }}>
                    <div
                        className="safe-area-title"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: '1.3'
                        }}
                    >
                        {store?.title ? (
                            <span style={{ fontSize: '15px', fontWeight: 'bold' }}>{store.title}</span>
                        ) : (
                            <div className="skeleton-shimmer" style={{ width: '80px', height: '14px', borderRadius: '4px', marginBottom: '4px' }} />
                        )}

                        {store?.message ? (
                            <span style={{ fontSize: '12px', opacity: 0.85 }}>{store.message}</span>
                        ) : store ? null : (
                            <div className="skeleton-shimmer" style={{ width: '120px', height: '10px', borderRadius: '4px' }} />
                        )}
                    </div>
                </TelegramSafeArea>

                <div className="scrollable-container" ref={scrollContainerRef} onScroll={handleBodyScroll}>
                    <div className={`topbar ${isScrolled ? 'scrolled' : ''}`}>
                        <StoreBanner store={store} isScrolled={isScrolled} />

                        <ShopHeader
                            color={color}
                            backgroundcolor={backgroundcolor}
                            shouldShowGroups={shouldShowGroups}
                            cartCount={cartItems.length ?? 0}
                            searchInput={searchInput}
                            setSearchInput={setSearchInput}
                            searchText={searchText}
                            isloadingSearch={isloading_search}
                            onExit={fn_Exit}
                            onSearch={fn_search}
                            onSearchClose={fn_search_close}
                        >
                            {shouldShowGroups && (
                                <ShopGroups
                                    groupsList={groupsList}
                                    isGroupsFinished={isGroupsFinished}
                                    selectedGroupId={selectedGroupId}
                                    onSelectGroup={handleGroupClick}
                                    onMouseDown={handleMouseDown}
                                    onMouseLeave={handleMouseLeave}
                                    onMouseUp={handleMouseUp}
                                    onMouseMove={handleMouseMove}
                                />
                            )}
                        </ShopHeader>
                    </div>

                    <div className="body-shop" style={{ background: backgroundcolor }}>
                        <div className="wrraper-shop">
                            <ShopEvents
                                isEventsLoaded={isEventsLoaded}
                                validEvents={validEvents}
                                cartCount={cartItems.length ?? 0}
                                onAdd={onAdd}
                                onOpenModal={(id) => {
                                    setCommodityId(id);
                                    setIsOpen(true);
                                }}
                                onMouseDown={handleMouseDown}
                                onMouseLeave={handleMouseLeave}
                                onMouseUp={handleMouseUp}
                                onMouseMove={handleMouseMove}
                            />

                            <ShopProductList
                                dataset={Dataset}
                                cartItems={cartItems}
                                isloadingSearch={isloading_search}
                                isLastPage={isLastPage}
                                nextCursor={nextCursor}
                                sentinelRef={sentinelRef}
                                onAdd={onAdd}
                                onOpenModal={(id) => {
                                    setCommodityId(id);
                                    setIsOpen(true);
                                }}
                            />
                        </div>
                    </div>
                </div>

                <ScrollToTopButton
                    show={showScrollTop}
                    onClick={scrollToTop}
                    themeColor={themeColor}
                />
            </div>
            
            {isOpen && (
                <ShopBasketAdd
                    onAdd={onAdd}
                    commodityId={commodityId}
                    storeId={activeStoreId}
                    store={store}
                    fn_close={fn_close}
                    isShow={isOpen}
                    badge={cartItems.length ?? 0}
                    isOpenSheet={isOpenSheet}
                />
            )}
        </>
    );
};

export default Shop;
